import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { useFormikContext } from "formik";
import AddButton from "../buttons/button";
import CloseIcon from "../../assets/icons/close-icon";
import IndianRupeeIcon from "../../assets/icons/indian-rupee";
import UsedBenefitsSummary from "./used-benefits-summary";
import { api } from "../../redux/apis/api";

// Fully read-only payment panel for VIEW mode (/view-bill/:id). The regular
// BillingPaymentPanel is an editing surface — payment-mode buttons, amount
// inputs, apply-benefit panels, Save/Print. None of that belongs on a saved
// bill, so view mode renders THIS panel instead (early return in
// billing-payment-panel.js): what was applied, what was paid, and the totals.
// Benefit identities come from GET /order/bill-benefits/:sot_id; amounts and
// payments come off the already-hydrated bill row in Formik values.

const fmtINR = (n) =>
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const Row = ({ label, value, bold = false, accent = false }) => (
  <div className="flex items-center justify-between px-4 py-2.5">
    <span
      className={`text-[13px] ${
        bold ? "font-bold text-[#0F172A]" : "text-[#475569]"
      }`}
    >
      {label}
    </span>
    <span
      className={`text-[13px] tabular-nums ${
        bold
          ? "font-bold text-[#0F172A]"
          : accent
          ? "font-semibold text-[#15803C]"
          : "font-medium text-[#0F172A]"
      }`}
    >
      {value}
    </span>
  </div>
);

export default function ViewBillPaymentPanel({ open, setOpen, prefixName = "sot" }) {
  const { values } = useFormikContext();
  const [benefits, setBenefits] = useState(null);

  const sotId = values?.[`${prefixName}_id`] || null;

  useEffect(() => {
    if (!open || !sotId) return;
    let cancelled = false;
    api
      .get(`/order/bill-benefits/${sotId}`)
      .then((res) => {
        if (!cancelled) setBenefits(res?.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setBenefits(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, sotId]);

  const schemeDisc =
    Number(benefits?.scheme?.amount ?? values?.sot_scheme_discount ?? 0) || 0;
  const couponDisc =
    Number(benefits?.coupon?.amount ?? values?.sot_coupon_discount ?? 0) || 0;
  const pointsDisc =
    Number(benefits?.points?.amount ?? values?.sot_points_discount ?? 0) || 0;
  const totalBenefit = schemeDisc + couponDisc + pointsDisc;
  // The bill row stores sot_total_amount NET of all benefits (the backend's
  // canonical overwrite subtracts coupon + scheme + points before INSERT),
  // but view/edit hydration reconstructs the PRE-benefit figure into Formik
  // so the interactive panels can re-apply their substitution. Trusting the
  // Formik value here double-counted the discount and showed a phantom Due.
  // Use the stored net from the endpoint; fall back to reconstruction-minus-
  // benefits only while the fetch is in flight / failed.
  const netPayable =
    benefits?.totals?.net != null
      ? Number(benefits.totals.net)
      : Number(values?.[`${prefixName}_total_amount`] || 0) - totalBenefit;
  const grossAmount = netPayable + totalBenefit;

  const payments = Array.isArray(values?.paymentData) ? values.paymentData : [];
  const totalPaid = payments.reduce(
    (s, p) => s + (Number(p?.payment_amount) || 0),
    0
  );

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-40" onClose={() => setOpen(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 flex justify-end">
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-in-out duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in-out duration-300"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel className="h-full relative w-[400px] sm:w-[600px] bg-white overflow-y-auto rounded-tl-lg rounded-bl-lg shadow-xl">
              {/* Header */}
              <div
                className="h-[61px] bg-[var(--slide-panel-head-bg)] px-3 flex items-center justify-between border-b"
                style={{ borderColor: "var(--slide-panel-border)" }}
              >
                <div
                  style={{
                    maxHeight: "32px",
                    color: "var(--slide-panel-h-fg)",
                    fontSize: "var(--slide-panel-h-fs)",
                    fontWeight: "var(--slide-panel-h-fw)",
                  }}
                  className="min-w-[75%]"
                >
                  Payment Details
                  <span className="ml-2 text-[11px] font-semibold uppercase tracking-wider text-[#64748B] bg-[#F1F5F9] border border-[#E2E8F0] rounded px-2 py-0.5 align-middle">
                    View only
                  </span>
                </div>
                <AddButton
                  onclkFn={() => setOpen(false)}
                  icon={
                    <CloseIcon
                      width="12px"
                      height="12px"
                      fill="var(--close-button-fg)"
                    />
                  }
                  text={
                    <span>
                      Close <span className="opacity-50">(X)</span>{" "}
                    </span>
                  }
                  className="flex h-[32px] items-center gap-2 bg-[var(--close-button-bg)] border-radius px-3 text-[var(--close-button-fg)]"
                />
              </div>

              <div className="px-3 pb-8 mt-4 overflow-y-auto">
                {/* Benefits applied on the bill */}
                <UsedBenefitsSummary sotId={sotId} />

                {/* Payments made */}
                <div className="border border-[var(--slide-panel-border)] rounded mb-4">
                  <div className="bg-[var(--payment-summary-head-bg)] text-[var(--payment-summary-head-fg)] text-[length:var(--payment-summary-head-fs)] font-[var(--payment-summary-head-fw)] w-full h-[44px] flex items-center px-4">
                    Payments made
                  </div>
                  {payments.length === 0 ? (
                    <div className="px-4 py-3 text-[13px] text-[#64748B]">
                      No payment records on this bill.
                    </div>
                  ) : (
                    <div className="divide-y divide-[#F1F5F9]">
                      {payments.map((pay, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between px-4 py-2.5"
                        >
                          <span className="flex items-center gap-2 text-[13px] font-medium text-[#0F172A]">
                            <IndianRupeeIcon fill="#475569" />
                            {pay?.payment_mode_name || "—"}
                            {/* transaction_id arrives as the STRING "null"
                                on cash rows — hide those too. */}
                            {pay?.transaction_id && pay.transaction_id !== "null" ? (
                              <span className="text-[11px] text-[#94A3B8] font-mono">
                                {pay.transaction_id}
                              </span>
                            ) : null}
                          </span>
                          <span className="text-[13px] font-semibold tabular-nums text-[#0F172A]">
                            ₹ {fmtINR(pay?.payment_amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Amount summary */}
                <div className="border border-[var(--slide-panel-border)] rounded">
                  <div className="bg-[var(--payment-summary-head-bg)] text-[var(--payment-summary-head-fg)] text-[length:var(--payment-summary-head-fs)] font-[var(--payment-summary-head-fw)] w-full h-[44px] flex items-center px-4">
                    Amount summary
                  </div>
                  <div className="divide-y divide-[#F1F5F9]">
                    {totalBenefit > 0 && (
                      <Row label="Original amount" value={`₹ ${fmtINR(grossAmount)}`} />
                    )}
                    {schemeDisc > 0 && (
                      <Row
                        label={`Scheme${benefits?.scheme?.name ? ` · ${benefits.scheme.name}` : ""}`}
                        value={`− ₹ ${fmtINR(schemeDisc)}`}
                        accent
                      />
                    )}
                    {couponDisc > 0 && (
                      <Row
                        label={`Coupon${benefits?.coupon?.code ? ` · ${benefits.coupon.code}` : ""}`}
                        value={`− ₹ ${fmtINR(couponDisc)}`}
                        accent
                      />
                    )}
                    {pointsDisc > 0 && (
                      <Row
                        label={`Loyalty points${
                          benefits?.points?.points != null
                            ? ` · ${benefits.points.points} pts`
                            : ""
                        }`}
                        value={`− ₹ ${fmtINR(pointsDisc)}`}
                        accent
                      />
                    )}
                    <Row label="Net amount" value={`₹ ${fmtINR(netPayable)}`} bold />
                    <Row label="Paid" value={`₹ ${fmtINR(totalPaid)}`} />
                    {netPayable - totalPaid > 0.005 && (
                      <Row
                        label="Due"
                        value={`₹ ${fmtINR(netPayable - totalPaid)}`}
                        bold
                      />
                    )}
                  </div>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
